/**
 * 카드 입력 칸 — 로그인한 결제 화면(`CardDialog`)과 결제 링크 화면(`/pay`)이 함께 쓴다 (261020-pay-link).
 * ★ 값은 부르는 화면의 상태로만 산다. `autoComplete="off"` 다 — 보낸 뒤 그 화면이 바로 지운다.
 * ★ 상태와 서버에는 **숫자만** 간다. 카드번호의 대시는 표시만 바꾼다
 */
import type { CardInput } from '@geo-admin/shared/contract/api/billing.ts';
import { Input, Label } from './ui/form.tsx';

export type CardForm = {
  pinNumber: string;
  expireDate: string;
  socialNumber: string;
  password: string;
  holderName: string;
  holderPhone: string;
};

export const BLANK_CARD: CardForm = {
  pinNumber: '',
  expireDate: '',
  socialNumber: '',
  password: '',
  holderName: '',
  holderPhone: '',
};

/** 보낼 모양 — 이름 앞뒤 공백을 걷고, 비운 휴대폰은 뺀다 */
export const cardInputOf = (f: CardForm): CardInput => ({
  ...f,
  holderName: f.holderName.trim(),
  holderPhone: f.holderPhone || undefined,
});

/** 카드번호 표시 — 4자리마다 대시(1234-5678-9012-3456) */
export const cardNumberText = (digits: string) => digits.replace(/(\d{4})(?=\d)/g, '$1-');

export function CardFields({
  value: f,
  onChange,
  inputClassName,
}: {
  value: CardForm;
  onChange: (next: CardForm) => void;
  /** 모바일 화면은 칸을 크게 쓴다 */
  inputClassName?: string;
}) {
  const set = (k: keyof CardForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...f, [k]: e.target.value.replace(k === 'holderName' ? /^\s+/ : /\D/g, '') });
  return (
    <>
      <Label>
        카드번호
        <Input
          className={inputClassName}
          inputMode="numeric"
          autoComplete="off"
          value={cardNumberText(f.pinNumber)}
          onChange={(e) => onChange({ ...f, pinNumber: e.target.value.replace(/\D/g, '').slice(0, 19) })}
          placeholder="1234-5678-9012-3456"
          maxLength={23}
          required
        />
      </Label>
      <div className="grid grid-cols-2 gap-3">
        <Label>
          유효기간 (MMYY)
          <Input
            className={inputClassName}
            inputMode="numeric"
            autoComplete="off"
            placeholder="0328"
            value={f.expireDate}
            onChange={set('expireDate')}
            maxLength={4}
            required
          />
        </Label>
        <Label>
          비밀번호 앞 2자리
          <Input
            className={inputClassName}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={f.password}
            onChange={set('password')}
            maxLength={2}
            required
          />
        </Label>
      </div>
      <Label>
        생년월일 6자리 (법인카드는 사업자번호 10자리)
        <Input
          className={inputClassName}
          inputMode="numeric"
          autoComplete="off"
          value={f.socialNumber}
          onChange={set('socialNumber')}
          maxLength={10}
          required
        />
      </Label>
      <Label>
        카드 소유주 이름
        <Input
          className={inputClassName}
          autoComplete="off"
          value={f.holderName}
          onChange={set('holderName')}
          maxLength={50}
          required
        />
      </Label>
      <Label>
        결제 알림톡 받을 휴대폰 (선택)
        <Input
          className={inputClassName}
          inputMode="numeric"
          autoComplete="off"
          placeholder="01012345678"
          value={f.holderPhone}
          onChange={set('holderPhone')}
          maxLength={11}
        />
      </Label>
    </>
  );
}
